import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Warehouse, Truck, Users, AlertTriangle, FileText, TrendingDown, Zap, BarChart3, ShoppingCart, Clock, CheckCircle2, ArrowRight } from 'lucide-react'
import { AddButton, ActionControls } from '../components/common/CRUDControls'

export default function InventorySupplyChainPage() {
  const navigate = useNavigate()
  const [stats] = useState({
    totalProducts: 2847,
    lowStockItems: 42,
    openOrders: 156,
    suppliers: 89,
  })

  const [topSuppliers, setTopSuppliers] = useState([
    { id: 1, name: 'Premier Logistics Co', onTime: 98, quality: 96, reliability: 97 },
    { id: 2, name: 'Global Parts Supply', onTime: 94, quality: 92, reliability: 93 },
    { id: 3, name: 'Manufacturing Corp', onTime: 91, quality: 89, reliability: 90 },
    { id: 4, name: 'Direct Distributors', onTime: 87, quality: 85, reliability: 86 },
  ])

  const handleAddSupplier = () => {
    const name = prompt("Enter Supplier Name")
    if (name) setTopSuppliers([...topSuppliers, { id: Date.now(), name, onTime: 90, quality: 90, reliability: 90 }])
  }

  const handleEditSupplier = (id) => {
    const s = topSuppliers.find(x => x.id === id)
    const newOnTime = prompt("Update On-Time %", s.onTime)
    if (newOnTime) setTopSuppliers(topSuppliers.map(x => x.id === id ? { ...x, onTime: parseInt(newOnTime) } : x))
  }

  const handleDeleteSupplier = (id) => {
    if (confirm("Remove supplier?")) setTopSuppliers(topSuppliers.filter(x => x.id !== id))
  }

  const supplyChainFeatures = [
    {
      title: '📦 Product Catalog',
      description: 'Manage product information, SKUs, barcodes, and product variants',
      icon: Package,
      color: 'from-blue-600 to-blue-500',
      stats: '2,847 Products',
      path: '/products'
    },
    {
      title: '🏢 Inventory Management',
      description: 'Real-time stock tracking across multiple warehouses and locations',
      icon: Warehouse,
      color: 'from-cyan-600 to-cyan-500',
      stats: '42 Low Stock',
      path: '/inventory'
    },
    {
      title: '🚚 Purchase Orders',
      description: 'Create, track, and manage purchase orders from suppliers',
      icon: ShoppingCart,
      color: 'from-indigo-600 to-indigo-500',
      stats: '156 Open Orders',
      path: '/purchase-orders'
    },
    {
      title: '👥 Supplier Management',
      description: 'Manage supplier contacts, agreements, and performance metrics',
      icon: Users,
      color: 'from-teal-600 to-teal-500',
      stats: '89 Suppliers',
      path: '/suppliers'
    },
    {
      title: '⚠️ Stock Alerts & Reordering',
      description: 'Automated low stock alerts and intelligent reorder recommendations',
      icon: AlertTriangle,
      color: 'from-orange-600 to-orange-500',
      stats: '24/7 Monitoring',
      path: '/stock-alerts'
    },
    {
      title: '📋 Warehouse Management',
      description: 'Organize and manage multiple warehouse locations and zones',
      icon: Warehouse,
      color: 'from-purple-600 to-purple-500',
      stats: '12 Warehouses',
      path: '/warehouses'
    },
    {
      title: '🚛 Shipment Tracking',
      description: 'Track shipments, deliveries, and logistics in real-time',
      icon: Truck,
      color: 'from-pink-600 to-pink-500',
      stats: '1,247 In Transit',
      path: '/shipments'
    },
    {
      title: '📊 Supply Chain Reports',
      description: 'Analytics on inventory turnover, supplier performance, and trends',
      icon: BarChart3,
      color: 'from-amber-600 to-amber-500',
      stats: 'Real-time Analytics',
      path: '/supply-reports'
    },
  ]

  const inventoryStatus = [
    { category: 'In Stock', count: 2805, status: 'healthy', color: 'text-green-400' },
    { category: 'Low Stock', count: 42, status: 'warning', color: 'text-yellow-400' },
    { category: 'Out of Stock', count: 8, status: 'critical', color: 'text-red-400' },
    { category: 'Discontinued', count: 12, status: 'inactive', color: 'text-blue-400' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Package className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Inventory & Supply Chain</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Enterprise-Grade Inventory Management & Supply Chain Optimization</p>
                <p className="text-blue-200 text-sm mt-1">Real-Time Tracking, Forecasting & Automated Ordering</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">2,847</div>
                <div className="text-sm text-blue-100">Active Products</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Products', value: stats.totalProducts, icon: Package, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Low Stock Items', value: stats.lowStockItems, icon: AlertTriangle, color: 'from-orange-600 to-orange-500', textColor: 'text-orange-400' },
            { label: 'Open Purchase Orders', value: stats.openOrders, icon: ShoppingCart, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
            { label: 'Active Suppliers', value: stats.suppliers, icon: Users, color: 'from-teal-600 to-teal-500', textColor: 'text-teal-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Inventory Status */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
            <Warehouse className="w-6 h-6 text-blue-400" />
            <span>Inventory Status Overview</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {inventoryStatus.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-4 border border-blue-500/20">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-bold">{item.category}</h3>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full bg-slate-600 ${item.color}`}>
                    {item.count}
                  </span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      item.status === 'healthy' ? 'bg-green-500' :
                      item.status === 'warning' ? 'bg-yellow-500' :
                      item.status === 'critical' ? 'bg-red-500' :
                      'bg-blue-500'
                    }`}
                    style={{width: `${(item.count / 2805) * 100}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Supply Chain Features Grid */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Package className="w-7 h-7 text-blue-400" />
            <span>Supply Chain Management Tools</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {supplyChainFeatures.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div
                  key={idx}
                  onClick={() => navigate(feature.path)}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Supplier Performance & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Suppliers */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Users className="w-5 h-5 text-blue-400" />
              <span>Top Supplier Performance</span>
            </h3>
            <AddButton onClick={handleAddSupplier} label="Add Supplier" />
            <div className="space-y-3 mt-3">
              {topSuppliers.map((supplier) => (
                <div key={supplier.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-white font-semibold text-sm">{supplier.name}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-bold px-2 py-1 rounded bg-green-500/20 text-green-300">Active</span>
                      <ActionControls onEdit={() => handleEditSupplier(supplier.id)} onDelete={() => handleDeleteSupplier(supplier.id)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-blue-300">On-Time</p>
                      <p className="text-green-400 font-bold">{supplier.onTime}%</p>
                    </div>
                    <div>
                      <p className="text-blue-300">Quality</p>
                      <p className="text-green-400 font-bold">{supplier.quality}%</p>
                    </div>
                    <div>
                      <p className="text-blue-300">Reliability</p>
                      <p className="text-green-400 font-bold">{supplier.reliability}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Critical Alerts */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <span>Critical Inventory Alerts</span>
            </h3>
            <div className="space-y-3">
              {[
                { item: 'SKU-2847 - Ball Bearings', alert: 'Low stock - 15 units left', priority: 'CRITICAL' },
                { item: 'SKU-1923 - Electrical Connectors', alert: 'Expected shortage in 3 days', priority: 'HIGH' },
                { item: 'SKU-5634 - Power Supply Units', alert: 'Supplier delay reported', priority: 'HIGH' },
                { item: 'SKU-3421 - Control Panel', alert: 'Below reorder point', priority: 'MEDIUM' },
              ].map((alert, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{alert.item}</p>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      alert.priority === 'CRITICAL' ? 'bg-red-500/20 text-red-300' :
                      alert.priority === 'HIGH' ? 'bg-orange-500/20 text-orange-300' :
                      'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {alert.priority}
                    </span>
                  </div>
                  <p className="text-blue-300 text-xs">{alert.alert}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ERP Integration Features */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
            <Zap className="w-6 h-6 text-blue-400" />
            <span>ERP Integration & Advanced Features</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: '🔄 Bill of Materials', desc: 'Create and manage product assembly structures' },
              { title: '📊 Demand Forecasting', desc: 'AI-powered inventory forecasting and planning' },
              { title: '💰 Cost Tracking', desc: 'Monitor unit costs, margins, and profitability' },
              { title: '🔗 Integration Hub', desc: 'Connect with ERPs, accounting, and fulfillment' },
              { title: '⚙️ Automation Rules', desc: 'Auto-trigger reorders, alerts, and workflows' },
              { title: '📈 Performance KPIs', desc: 'Track inventory turnover, carrying costs, ROI' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                <p className="text-white font-bold mb-1">{feature.title}</p>
                <p className="text-blue-300 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
